import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../../auth/useAuth0';
import { useBusinessContext } from '../../../../context/BusinessContext';
import { useAlert } from '../../../../context/alert/AlertContext';
import LoadingComponent from '../../../commons/LoadingComponent';
import Drawer from '../../../commons/drawer/Drawer';
import ConfirmModal from '../../../commons/confirm-modal/ConfirmModal';
import SearchableSelect, { SelectOption } from '../../../commons/searchable-select/SearchableSelect';
import { Service } from '../../../../model/service/Service';
import { TeamMember } from '../../../../model/teammember/TeamMember';
import { AppointmentStatus } from '../../../../model/appointment/AppointmentStatus';
import { getServicesByBusinessId } from '../../../../service/service/service-service';
import {
    getTeamMembersByBusinessId,
    saveTeamMember,
} from '../../../../service/teammember/team-member-service';
import { getAppointmentsByTeamMember } from '../../../../service/appointment/appointment-service';
import { TeamMemberInput } from '../../../../service/teammember/TeamMemberInput';
import { resolveTeamMemberColor, readableTextColor } from '../../../../util/teamMemberColor';
import './ProfessionalsTab.css';

const TERMINAL_STATUSES: AppointmentStatus[] = ['CANCELLED', 'DECLINED', 'NO_SHOW', 'COMPLETED'];

/** Rebuilds the FULL TeamMemberInput, overriding only serviceIds (saveTeamMember
 *  replaces the whole record, so every field must be carried over). */
function toInput(tm: TeamMember, businessId: number, serviceIds: string[]): TeamMemberInput {
    return {
        id: tm.id,
        firstName: tm.firstName,
        lastName: tm.lastName,
        email: tm.email ?? null,
        phone: tm.phone ?? null,
        color: tm.color ?? null,
        businessId: String(businessId),
        allowOnlineBooking: tm.allowOnlineBooking,
        hasAppAccess: tm.hasAppAccess ?? false,
        roleId: tm.roleId ?? null,
        roleBusinessId: tm.roleBusinessId ?? null,
        workingHours: (tm.workingHours ?? []).map((w) => ({
            id: w.id,
            dayOfWeek: w.dayOfWeek,
            startTime: w.startTime,
            endTime: w.endTime,
            allowBookingOnIntervalTime: w.allowBookingOnIntervalTime,
            startIntervalTime: w.startIntervalTime ?? null,
            endIntervalTime: w.endIntervalTime ?? null,
            overnightEnd: w.overnightEnd ?? null,
        })),
        serviceIds,
    };
}

const fullName = (tm: TeamMember) => `${tm.firstName ?? ''} ${tm.lastName ?? ''}`.trim();
const initialsOf = (tm: TeamMember) =>
    `${(tm.firstName?.[0] ?? '?')}${tm.lastName?.[0] ?? ''}`.toUpperCase();

interface RemoveTarget {
    tm: TeamMember;
    serviceId: string;
    serviceName: string;
}

/**
 * "Profissional × Serviço" tab — a professional LIST (search + click) where each
 * row opens a DRAWER to manage that professional's services: add (searchable),
 * remove (with confirmation + a check for upcoming appointments). Same drawer
 * pattern as the Team / Services / Clients editors.
 */
const ProfessionalsTab: React.FC = () => {
    const { t } = useTranslation();
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setAlert } = useAlert();
    const businessId = Number(selectedBusiness?.id) || 0;

    const [services, setServices] = useState<Service[]>([]);
    const [members, setMembers] = useState<TeamMember[]>([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [filterSvc, setFilterSvc] = useState('');
    const [openMemberId, setOpenMemberId] = useState<string | null>(null);
    const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);
    const [removeBusy, setRemoveBusy] = useState(false);

    const load = useCallback(async () => {
        if (!businessId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const token = await getAccessTokenSilently();
            const [svc, mem] = await Promise.all([
                getServicesByBusinessId(businessId, token),
                getTeamMembersByBusinessId(businessId, token),
            ]);
            // Equipe×Serviço lists SERVICES only — events pick their pros in the event drawer.
            setServices((svc ?? []).filter((sv) => (sv.kind ?? 'SERVICE') !== 'EVENT'));
            setMembers(mem);
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('services.professionals.loadError'));
        } finally {
            setLoading(false);
        }
    }, [businessId, getAccessTokenSilently, setAlert, t]);

    useEffect(() => {
        load();
    }, [load]);

    const serviceById = useMemo(() => new Map(services.map((s) => [s.id, s])), [services]);
    const validIds = useCallback(
        (ids?: string[] | null): string[] =>
            Array.from(new Set(ids ?? [])).filter((id) => serviceById.has(id)),
        [serviceById],
    );
    const isOnline = (id: string) => serviceById.get(id)?.allowOnlineBooking !== false;

    /** Persist a member's new service list (full-input save), optimistic + revert. */
    const persist = async (tm: TeamMember, nextIds: string[]) => {
        setSavingId(tm.id);
        const prevIds = tm.serviceIds;
        setMembers((prev) => prev.map((m) => (m.id === tm.id ? { ...m, serviceIds: nextIds } : m)));
        try {
            const token = await getAccessTokenSilently();
            await saveTeamMember(toInput(tm, businessId, nextIds), token);
        } catch (e) {
            setMembers((prev) => prev.map((m) => (m.id === tm.id ? { ...m, serviceIds: prevIds } : m)));
            setAlert('danger', e instanceof Error ? e.message : t('services.professionals.saveError'));
        } finally {
            setSavingId(null);
        }
    };

    const addService = (tm: TeamMember, serviceId: string) => {
        if (!serviceId) return;
        persist(tm, [...validIds(tm.serviceIds), serviceId]);
    };

    /** Validate then open the remove confirmation. A service the professional
     *  still has FUTURE appointments for cannot be removed (hard block) — the
     *  appointment check runs FIRST. */
    const askRemove = async (tm: TeamMember, serviceId: string) => {
        const serviceName = serviceById.get(serviceId)?.name ?? serviceId;
        // 1) Block if there are upcoming (scheduled) appointments for this service.
        try {
            const token = await getAccessTokenSilently();
            const appts = await getAppointmentsByTeamMember(tm.id, token);
            const now = Date.now();
            const upcoming = appts.filter(
                (a) =>
                    a.serviceId === serviceId &&
                    !TERMINAL_STATUSES.includes(a.status) &&
                    new Date(a.startTime).getTime() >= now,
            ).length;
            if (upcoming > 0) {
                setAlert('danger', t('services.professionals.removeBlockedAppts', { count: upcoming }));
                return;
            }
        } catch {
            // If we can't verify, fail safe: block and tell the user to retry.
            setAlert('danger', t('services.professionals.removeCheckFailed'));
            return;
        }
        // 2) An online-bookable professional can't be left with zero services.
        const remaining = validIds(tm.serviceIds).filter((id) => id !== serviceId);
        if (remaining.length === 0 && tm.allowOnlineBooking) {
            setAlert('danger', t('services.professionals.onlineNeedsService'));
            return;
        }
        // 3) Allowed → confirm.
        setRemoveTarget({ tm, serviceId, serviceName });
    };

    const confirmRemove = async () => {
        if (!removeTarget) return;
        setRemoveBusy(true);
        await persist(removeTarget.tm, validIds(removeTarget.tm.serviceIds).filter((id) => id !== removeTarget.serviceId));
        setRemoveBusy(false);
        setRemoveTarget(null);
    };

    // ---- list ----
    const activeMembers = useMemo(
        () =>
            members
                .filter((m) => m.active !== false)
                .slice()
                .sort((a, b) => fullName(a).localeCompare(fullName(b), undefined, { sensitivity: 'base' })),
        [members],
    );
    // Both filters combine (AND): name search + "performs this service".
    const filteredMembers = useMemo(() => {
        const q = search.trim().toLowerCase();
        return activeMembers.filter((m) => {
            if (q && !fullName(m).toLowerCase().includes(q)) return false;
            if (filterSvc && !validIds(m.serviceIds).includes(filterSvc)) return false;
            return true;
        });
    }, [activeMembers, search, filterSvc, validIds]);

    const svcFilterOptions: SelectOption[] = useMemo(
        () => [
            { value: '', label: t('services.professionals.allServices') },
            ...services
                .slice()
                .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }))
                .map((s) => ({ value: s.id, label: s.name ?? '' })),
        ],
        [services, t],
    );

    const openMember = useMemo(
        () => members.find((m) => m.id === openMemberId) ?? null,
        [members, openMemberId],
    );

    if (loading) return <LoadingComponent />;

    return (
        <div className="pros-tab" data-cy="professionals-tab">
            <div className="list-card" data-cy="professionals-list">
                <div className="list-header">
                    <h5 className="mb-0">{t('services.professionals.title')}</h5>
                    <div className="gs-list-search">
                        <span className="gs-list-search__icon" aria-hidden>⌕</span>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={t('services.professionals.searchPlaceholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-cy="pros-search"
                        />
                        {search && (
                            <button
                                type="button"
                                className="gs-list-search__clear"
                                onClick={() => setSearch('')}
                                aria-label={t('common.clear')}
                                title={t('common.clear')}
                            >
                                ×
                            </button>
                        )}
                    </div>
                    {/* Service filter — combines with the name search (AND). */}
                    <div className="pros-svc-filter">
                        <SearchableSelect
                            options={svcFilterOptions}
                            value={filterSvc}
                            onChange={setFilterSvc}
                            placeholder={t('services.professionals.allServices')}
                            dataCy="pros-filter-service"
                        />
                    </div>
                </div>

                {filteredMembers.length === 0 && (
                    <div className="empty-hint">{t('services.professionals.empty')}</div>
                )}

                {filteredMembers.map((tm, idx) => {
                    const ids = validIds(tm.serviceIds);
                    const onlineCount = ids.filter(isOnline).length;
                    const color = resolveTeamMemberColor(tm, idx);
                    return (
                        <div
                            key={tm.id}
                            className="pros-prof-row"
                            onClick={() => setOpenMemberId(tm.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') setOpenMemberId(tm.id);
                            }}
                            data-cy={`pros-prof-row-${tm.id}`}
                        >
                            <span
                                className="pros-prof-row__avatar"
                                style={{ background: color, color: readableTextColor(color) }}
                                aria-hidden="true"
                            >
                                {initialsOf(tm)}
                            </span>
                            <span className="pros-prof-row__info">
                                <span className="pros-prof-row__name">{fullName(tm)}</span>
                                <span className="pros-prof-row__sub">
                                    {t('services.professionals.count', { count: ids.length })}
                                    {ids.length > 0 && (
                                        <> · {t('services.professionals.onlineCount', { count: onlineCount })}</>
                                    )}
                                    {!tm.allowOnlineBooking && (
                                        <> · {t('services.professionals.offline')}</>
                                    )}
                                </span>
                            </span>
                            <span className="pros-prof-row__chev" aria-hidden>›</span>
                        </div>
                    );
                })}
            </div>

            {/* Drawer — manage one professional's services */}
            <Drawer
                open={!!openMember}
                onClose={() => setOpenMemberId(null)}
                title={openMember ? t('services.professionals.drawerTitle', { name: fullName(openMember) }) : ''}
                subtitle={t('services.professionals.drawerSubtitle')}
                footer={
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setOpenMemberId(null)}
                    >
                        {t('common.close')}
                    </button>
                }
            >
                {openMember && (() => {
                    const ids = validIds(openMember.serviceIds);
                    const available: SelectOption[] = services
                        .filter((s) => !ids.includes(s.id))
                        .map((s) => ({ value: s.id, label: s.name ?? '' }));
                    const busy = savingId === openMember.id;
                    return (
                        <div className="pros-drawer">
                            <label className="pros-drawer__add-label">
                                {t('services.professionals.addService')}
                            </label>
                            <SearchableSelect
                                options={available}
                                value=""
                                onChange={(v) => addService(openMember, v)}
                                placeholder={
                                    available.length > 0
                                        ? t('services.professionals.addService')
                                        : t('services.professionals.allAdded')
                                }
                                isDisabled={busy || available.length === 0}
                                dataCy="pros-drawer-add"
                            />

                            <div className="pros-drawer__list">
                                {ids.length === 0 ? (
                                    <div className="pros-drawer__empty">
                                        {t('services.professionals.noneInDrawer')}
                                    </div>
                                ) : (
                                    ids
                                        .slice()
                                        .sort((a, b) =>
                                            (serviceById.get(a)?.name ?? '').localeCompare(
                                                serviceById.get(b)?.name ?? '',
                                                undefined,
                                                { sensitivity: 'base' },
                                            ),
                                        )
                                        .map((id) => {
                                            const svc = serviceById.get(id);
                                            const online = isOnline(id);
                                            return (
                                                <div className="pros-svc-line" key={id} data-cy={`pros-drawer-svc-${id}`}>
                                                    <span
                                                        className="pros-svc-line__swatch"
                                                        style={{ background: svc?.color ?? '#ccc' }}
                                                        aria-hidden
                                                    />
                                                    <span className="pros-svc-line__name">{svc?.name ?? id}</span>
                                                    <span
                                                        className={`pros-avail ${online ? 'pros-avail--online' : 'pros-avail--inperson'}`}
                                                    >
                                                        {online
                                                            ? t('services.professionals.online')
                                                            : t('services.professionals.inPerson')}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm pros-svc-line__remove"
                                                        onClick={() => askRemove(openMember, id)}
                                                        disabled={busy}
                                                        data-cy={`pros-drawer-remove-${id}`}
                                                    >
                                                        {t('services.professionals.remove')}
                                                    </button>
                                                </div>
                                            );
                                        })
                                )}
                            </div>
                        </div>
                    );
                })()}
            </Drawer>

            {/* Remove confirmation (with upcoming-appointment warning) */}
            <ConfirmModal
                open={!!removeTarget}
                title={t('services.professionals.removeConfirmTitle')}
                message={
                    removeTarget
                        ? t('services.professionals.removeConfirmMsg', {
                              service: removeTarget.serviceName,
                              name: fullName(removeTarget.tm),
                          })
                        : ''
                }
                confirmLabel={t('services.professionals.remove')}
                danger
                busy={removeBusy}
                onConfirm={confirmRemove}
                onCancel={() => setRemoveTarget(null)}
                dataCy="pros-remove-confirm"
            />
        </div>
    );
};

export default ProfessionalsTab;
